import Icon from '@/components/ui/AppIcon';

interface HeroSectionProps {
  onScheduleConsultation: () => void;
}

const HeroSection = ({ onScheduleConsultation }: HeroSectionProps) => {
  return (
    <section className="relative bg-gradient-to-br from-primary via-primary/95 to-secondary text-primary-foreground overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-10 w-72 h-72 bg-accent rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-success rounded-full blur-3xl"></div>
      </div>
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-8">
            <div className="inline-flex items-center space-x-2 bg-accent/20 backdrop-blur-sm px-4 py-2 rounded-full">
              <Icon name="SparklesIcon" size={20} className="text-accent" />
              <span className="text-sm font-semibold">AI-Powered Marketing Excellence</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold leading-tight">
              Transform Marketing Into a
              <span className="text-accent"> Revenue Engine</span>
            </h1>
            
            <p className="text-lg sm:text-xl text-primary-foreground/90 leading-relaxed">
              Scale your marketing operations with intelligent automation that delivers measurable ROI. From campaign orchestration to lead nurturing, our AI systems turn marketing complexity into competitive advantage.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={onScheduleConsultation}
                className="px-8 py-4 bg-accent text-accent-foreground font-heading font-semibold rounded-lg hover:bg-accent/90 hover:shadow-elevated transition-all duration-300 transform hover:-translate-y-0.5 flex items-center justify-center space-x-2"
              >
                <span>Schedule Strategy Call</span>
                <Icon name="ArrowRightIcon" size={20} />
              </button>
              
              <button className="px-8 py-4 bg-primary-foreground/10 backdrop-blur-sm text-primary-foreground font-heading font-semibold rounded-lg hover:bg-primary-foreground/20 transition-all duration-300 flex items-center justify-center space-x-2">
                <Icon name="CalculatorIcon" size={20} />
                <span>Calculate Your ROI</span>
              </button>
            </div>
            
            <div className="flex items-center space-x-8 pt-4">
              <div className="flex items-center space-x-2">
                <Icon name="CheckCircleIcon" size={24} className="text-success" variant="solid" />
                <span className="text-sm font-semibold">3x Campaign Efficiency</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="CheckCircleIcon" size={24} className="text-success" variant="solid" />
                <span className="text-sm font-semibold">45% Cost Reduction</span>
              </div>
            </div>
          </div>
          
          <div className="relative">
            <div className="relative bg-card/10 backdrop-blur-sm rounded-2xl p-8 border border-primary-foreground/20">
              <div className="space-y-6">
                <div className="flex items-center justify-between p-4 bg-primary-foreground/10 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-accent rounded-lg flex items-center justify-center">
                      <Icon name="EnvelopeIcon" size={24} className="text-accent-foreground" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold">Email Campaigns</p>
                      <p className="text-xs text-primary-foreground/70">Automated & Personalized</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-success">+156%</p>
                    <p className="text-xs text-primary-foreground/70">Open Rate</p>
                  </div>
                </div>
                
                <div className="flex items-center justify-between p-4 bg-primary-foreground/10 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-success rounded-lg flex items-center justify-center">
                      <Icon name="ChartBarIcon" size={24} className="text-success-foreground" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold">Lead Scoring</p>
                      <p className="text-xs text-primary-foreground/70">AI-Driven Qualification</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-success">+89%</p>
                    <p className="text-xs text-primary-foreground/70">Accuracy</p>
                  </div>
                </div>
                
                <div className="flex items-center justify-between p-4 bg-primary-foreground/10 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center">
                      <Icon name="UserGroupIcon" size={24} className="text-primary-foreground" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold">Audience Segmentation</p>
                      <p className="text-xs text-primary-foreground/70">Dynamic & Behavioral</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-success">+234%</p>
                    <p className="text-xs text-primary-foreground/70">Engagement</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;